/**
 * What the start step hands to the steps after it when the reader presses Next.
 *
 * The start step keeps its answer in LOCAL state and commits it to the session.
 * The steps after it render from the CONTEXT. Committing to the session alone
 * left the context holding whatever an earlier pass had put there — the old
 * team after a player went back and picked another, and no division at all
 * after a team-first pick (which deliberately never answers the division, so
 * the grid does not re-scope mid-click).
 *
 * Writing the commit into the context as well fixes that at the source: the
 * start step is the only live forward path into the register step (sign-up,
 * login and confirm-email all route back here; the retired select-role screen
 * also targets it, but `Main` redirects that screen to this step before it can
 * act), so the context is right on arrival by construction, with no
 * session-to-context reconciling needed to repair it.
 */

import type {
  DivisionTeam,
  StartDivision,
  StartTeam,
} from '@sportninja/common/features/registration/startStep/types'

export interface CommittedSelection<T extends StartDivision> {
  team: StartTeam | null
  division: T | null
}

export const committedSelection = <T extends StartDivision>({
  selectedDivision,
  selectedTeam,
  isTeamRole,
  divisions,
}: {
  selectedDivision: T | null
  selectedTeam: DivisionTeam<T> | null
  isTeamRole: boolean
  /** The registration's own division list — the canonical copy of each. */
  divisions: T[] | null | undefined
}): CommittedSelection<T> => {
  // The same division `handleNext` commits: the one answered directly, or else
  // the one the chosen team belongs to.
  const chosen = selectedDivision ?? selectedTeam?.division ?? null

  // Resolved against the registration's division list by `schedule_id` — the
  // key the session stores — so the register step renders the SAME object the
  // reload path gives it (`setSessionData` resolves it this way too), not the
  // copy embedded on a team entry.
  const division = chosen?.schedule_id
    ? divisions?.find((d) => d.schedule_id === chosen.schedule_id) ?? chosen
    : chosen

  return {
    // A rep never carries a team out of this step: the teams tab is closed to
    // them, and their team is chosen on the register step instead — where a
    // team they committed before is restored from the session. Handing over
    // null also drops a PLAYER's team left in context by a reader who switched
    // roles.
    team: isTeamRole ? null : selectedTeam?.team ?? null,
    division,
  }
}
